// =============================================================================
// VandieZ admin-web -- Cancellation charges (/cancellation/policies)
// =============================================================================
// PR-12 / migration 0084. What it costs to cancel, per lifecycle stage
// and per who cancelled. Before this the charge was two constants in
// the backend applied at ONE tier -- free until a bid was accepted,
// then MIN(5%, Rs.500) forever after, identical whether the driver had
// merely accepted or had already loaded the goods.
//
// Presented as a GRID rather than the flat rule list the commission
// page uses, because the thing a founder actually wants to decide is
// "does walking away later cost more than walking away early", and that
// is a question about the shape of a whole column, not about one row.
//
// Every cell shows a worked example against a sample load value. A rate
// and a cap are hard to weigh against each other in the abstract; "on a
// Rs.50,000 load this costs Rs.500" is not.
//
// Superseded, never overwritten: saving closes the active row and opens
// a new one, so what a past cancellation cost stays provable.
// =============================================================================

import { AlertCircle, IndianRupee, Loader2, Pencil, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { cancellationAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import {
  ROLE_LABEL,
  STAGE_HINT,
  STAGE_LABEL,
  type CancellationPolicy,
  type CancellationRole,
  type CancellationStage,
} from "@/types/cancellation";

/** The load value the worked examples are computed against. */
const SAMPLE_LOAD_INR = 50000;

function charge(policy: CancellationPolicy, amount: number): number {
  const rate = Number.parseFloat(policy.rate);
  const flat = Number.parseFloat(policy.flat_inr);
  const min = Number.parseFloat(policy.min_inr);
  const cap = policy.cap_inr === null ? null : Number.parseFloat(policy.cap_inr);

  let value = amount * rate + flat;
  if (value < min) {value = min;}
  if (cap !== null && value > cap) {value = cap;}
  return value < 0 ? 0 : value;
}

/** "5% capped ₹500", "₹250 flat", "Free" — the rule in words. */
function describe(policy: CancellationPolicy): string {
  const rate = Number.parseFloat(policy.rate);
  const flat = Number.parseFloat(policy.flat_inr);
  const min = Number.parseFloat(policy.min_inr);
  const cap = policy.cap_inr === null ? null : Number.parseFloat(policy.cap_inr);

  if (rate === 0 && flat === 0 && min === 0) {return "Free";}

  const parts: string[] = [];
  if (rate > 0) {parts.push(`${(rate * 100).toFixed(2).replace(/\.?0+$/, "")}%`);}
  if (flat > 0) {parts.push(`+ ₹${flat.toLocaleString("en-IN")}`);}
  let text = parts.join(" ") || "₹0";
  if (min > 0) {text += `, min ₹${min.toLocaleString("en-IN")}`;}
  text += cap === null ? ", uncapped" : `, capped ₹${cap.toLocaleString("en-IN")}`;
  return text;
}

function rupees(value: number): string {
  return `₹${value.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

interface EditState {
  stage: CancellationStage;
  role: CancellationRole;
  ratePct: string;
  flat: string;
  min: string;
  cap: string;
  uncapped: boolean;
  collect: boolean;
  counterpartyPct: string;
  notes: string;
}

export function CancellationPolicyPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [policies, setPolicies] = useState<CancellationPolicy[]>([]);
  const [stages, setStages] = useState<CancellationStage[]>([]);
  const [roles, setRoles] = useState<CancellationRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const data = await cancellationAdminApi.list(
          accessToken,
          false,
          signal,
        );
        setPolicies(data.items);
        setStages(data.stages);
        setRoles(data.roles);
      } catch (err) {
        if (signal?.aborted === true) {return;}
        setError(
          err instanceof ApiError ? err.message : "Could not load policies.",
        );
      } finally {
        if (signal?.aborted !== true) {setLoading(false);}
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => { controller.abort(); };
  }, [load]);

  /** (stage, role) -> the active policy, for O(1) cell lookup. */
  const grid = useMemo(() => {
    const map = new Map<string, CancellationPolicy>();
    for (const p of policies) {map.set(`${p.stage}|${p.cancelled_by_role}`, p);}
    return map;
  }, [policies]);

  const openEditor = useCallback(
    (stage: CancellationStage, role: CancellationRole) => {
      const current = grid.get(`${stage}|${role}`);
      setEdit({
        stage,
        role,
        ratePct:
          current === undefined
            ? "0"
            : (Number.parseFloat(current.rate) * 100).toString(),
        flat: current === undefined ? "0" : current.flat_inr,
        min: current === undefined ? "0" : current.min_inr,
        cap: current?.cap_inr ?? "",
        uncapped: current?.cap_inr === null,
        collect: current?.collect ?? true,
        counterpartyPct:
          current === undefined
            ? "0"
            : (Number.parseFloat(current.counterparty_share) * 100).toString(),
        notes: "",
      });
    },
    [grid],
  );

  const save = useCallback(async () => {
    if (edit === null || accessToken === null) {return;}
    setSaving(true);
    setError(null);
    try {
      const ratePct = Number.parseFloat(edit.ratePct);
      await cancellationAdminApi.create(accessToken, {
        stage: edit.stage,
        cancelled_by_role: edit.role,
        // The form takes a PERCENTAGE because that is how a founder
        // says it; the API takes a fraction. Converting here rather
        // than asking anyone to type 0.05 for 5%.
        rate: (Number.isFinite(ratePct) ? ratePct / 100 : 0).toString(),
        flat_inr: edit.flat === "" ? "0" : edit.flat,
        min_inr: edit.min === "" ? "0" : edit.min,
        cap_inr: edit.uncapped || edit.cap === "" ? null : edit.cap,
        collect: edit.collect,
        counterparty_share: (
          (Number.parseFloat(edit.counterpartyPct) || 0) / 100
        ).toString(),
        ...(edit.notes.trim() !== "" ? { notes: edit.notes.trim() } : {}),
      });
      setEdit(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }, [edit, accessToken, load]);

  if (loading && policies.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cancellation charges"
        description="What it costs to cancel, by how far the trip had got and who walked away."
      />

      {error !== null ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Alert>
        <IndianRupee className="h-4 w-4" />
        <AlertDescription>
          Examples are shown against a sample load of{" "}
          <strong>{rupees(SAMPLE_LOAD_INR)}</strong>. Saving supersedes the
          current charge rather than overwriting it, so past cancellations stay
          explainable.{" "}
          <strong>These charges are debited from the wallet.</strong> A
          wallet that cannot cover one goes negative, which already means
          &ldquo;owes the platform&rdquo; &mdash; the driver is blocked from
          bidding until they top up, so a cancellation is never refused for
          lack of funds.
        </AlertDescription>
      </Alert>

      {stages.map((stage) => (
        <Card key={stage}>
          <CardHeader>
            <CardTitle className="text-base">{STAGE_LABEL[stage]}</CardTitle>
            <CardDescription>{STAGE_HINT[stage]}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {roles.map((role) => {
                const policy = grid.get(`${stage}|${role}`);
                const isFree =
                  policy !== undefined &&
                  charge(policy, SAMPLE_LOAD_INR) === 0;
                return (
                  <button
                    type="button"
                    key={role}
                    onClick={() => { openEditor(stage, role); }}
                    className={cn(
                      "group rounded-lg border p-3 text-left transition",
                      "hover:border-primary hover:bg-muted/40",
                      policy === undefined && "border-dashed",
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">
                        {ROLE_LABEL[role]}
                      </span>
                      <Pencil className="h-3 w-3 opacity-0 transition group-hover:opacity-60" />
                    </div>
                    {policy === undefined ? (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Not configured — falls back to 5% capped ₹500
                      </p>
                    ) : (
                      <>
                        <p
                          className={cn(
                            "mt-1 text-lg font-semibold",
                            isFree && "text-muted-foreground",
                          )}
                        >
                          {isFree
                            ? "Free"
                            : rupees(charge(policy, SAMPLE_LOAD_INR))}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {describe(policy)}
                        </p>
                        {!policy.collect ? (
                          <p className="mt-1 text-xs font-medium text-warning">
                            Shown but not charged
                          </p>
                        ) : Number.parseFloat(policy.counterparty_share) >
                          0 ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {Math.round(
                              Number.parseFloat(policy.counterparty_share) *
                                100,
                            )}
                            % to the other party
                          </p>
                        ) : null}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {edit !== null ? (
        <Card className="border-primary">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-base">
                  {ROLE_LABEL[edit.role]} — {STAGE_LABEL[edit.stage]}
                </CardTitle>
                <CardDescription>
                  This closes the current charge and opens a new one.
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setEdit(null); }}
                aria-label="Close editor"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1">
                <Label htmlFor="rate">Rate (%)</Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={edit.ratePct}
                  onChange={(e) =>
                    { setEdit({ ...edit, ratePct: e.target.value }); }
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="flat">Flat (₹)</Label>
                <Input
                  id="flat"
                  type="number"
                  min="0"
                  value={edit.flat}
                  onChange={(e) => { setEdit({ ...edit, flat: e.target.value }); }}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="min">Minimum (₹)</Label>
                <Input
                  id="min"
                  type="number"
                  min="0"
                  value={edit.min}
                  onChange={(e) => { setEdit({ ...edit, min: e.target.value }); }}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cap">Cap (₹)</Label>
                <Input
                  id="cap"
                  type="number"
                  min="0"
                  disabled={edit.uncapped}
                  value={edit.uncapped ? "" : edit.cap}
                  placeholder={edit.uncapped ? "Uncapped" : ""}
                  onChange={(e) => { setEdit({ ...edit, cap: e.target.value }); }}
                />
                <label className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={edit.uncapped}
                    onChange={(e) =>
                      { setEdit({ ...edit, uncapped: e.target.checked }); }
                    }
                  />
                  No cap
                </label>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="cpshare">Paid to the other party (%)</Label>
                <Input
                  id="cpshare"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={edit.counterpartyPct}
                  onChange={(e) => {
                    setEdit({ ...edit, counterpartyPct: e.target.value });
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  0% keeps the whole charge as a platform fee. Above 0% it
                  becomes compensation for whoever was left stranded &mdash;
                  the driver when a shipper cancels, the shipper when a
                  driver does. Ignored when nobody had committed yet.
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="collect">Charge the wallet</Label>
                <label className="flex items-center gap-2 pt-2 text-sm">
                  <input
                    id="collect"
                    type="checkbox"
                    checked={edit.collect}
                    onChange={(e) => {
                      setEdit({ ...edit, collect: e.target.checked });
                    }}
                  />
                  Debit this charge
                </label>
                <p className="text-xs text-muted-foreground">
                  Unticked, the charge is still calculated and shown to the
                  user but no money moves. Use this to stop collection
                  without a deploy.
                </p>
              </div>
            </div>

            <div className="rounded-md bg-muted/50 p-3 text-sm">
              On a {rupees(SAMPLE_LOAD_INR)} load this would charge{" "}
              <strong>
                {rupees(
                  charge(
                    {
                      rate: (
                        (Number.parseFloat(edit.ratePct) || 0) / 100
                      ).toString(),
                      flat_inr: edit.flat === "" ? "0" : edit.flat,
                      min_inr: edit.min === "" ? "0" : edit.min,
                      cap_inr:
                        edit.uncapped || edit.cap === "" ? null : edit.cap,
                    } as CancellationPolicy,
                    SAMPLE_LOAD_INR,
                  ),
                )}
              </strong>
              .
            </div>

            <div className="space-y-1">
              <Label htmlFor="notes">Why (optional, kept on the record)</Label>
              <Textarea
                id="notes"
                rows={2}
                value={edit.notes}
                onChange={(e) => { setEdit({ ...edit, notes: e.target.value }); }}
                placeholder="e.g. Raised after repeated late cancellations on the Chennai lane"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={() => void save()} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : null}
                Save charge
              </Button>
              <Button variant="outline" onClick={() => { setEdit(null); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
